import React, { useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button, Input, MoneyInput } from '../../components/v2/V2Components';
import BulkImportPanel from '../../components/v2/BulkImportPanel';
import MortgageService from '../../services/MortgageService';
import { toDateInputValue } from '../../utils/billFormat';
import { formatMoney, toSubmittableMoney } from '../../utils/numericInput';
import { formatCycle } from '../../utils/escrowDisclosures';

const LABEL_CLASS = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1';

// Money fields the fix card shows for correction, in reading order.
const MONEY_FIELDS = [
  ['new_escrow_payment', 'New Escrow Payment', { required: true }],
  ['previous_escrow_payment', 'Previous Escrow Payment'],
  ['property_taxes_annual', 'Property Taxes / Year'],
  ['homeowners_insurance_annual', 'Homeowners Insurance / Year'],
  ['mortgage_insurance_annual', 'Mortgage Insurance / Year'],
];
// Read values the card doesn't show; carried through on save so fixing the
// cycle start doesn't silently drop them. Only values the server will accept
// are carried: the user has no input to correct one it would reject.
const CARRIED_MONEY_FIELDS = [
  'new_total_payment',
  'previous_total_payment',
  'shortage_amount',
  'surplus_amount',
  'required_cushion',
  'total_annual_disbursements',
  'other_annual',
];
const FIELD_LABELS = {
  cycle_start: 'New payment starts',
  statement_date: 'Analysis date',
  ...Object.fromEntries(MONEY_FIELDS.map(([key, label]) => [key, label])),
};

const isNumeric = (value) => value !== '' && Number.isFinite(Number(value));
// The reader may return the cycle as "YYYY-MM"; a date input needs a full day.
// Both forms go through toDateInputValue, so "2026-13" becomes '' (empty, and
// caught by the required check) instead of a value the input shows as blank.
const toCycleInputValue = (value) =>
  toDateInputValue(typeof value === 'string' && /^\d{4}-\d{2}$/.test(value) ? `${value}-01` : value);

const problemText = (result) => {
  const fields = (result.errors || []).map((err) => FIELD_LABELS[err.loc?.[0]] || err.loc?.[0]).filter(Boolean);
  return fields.length ? `${result.error}: ${[...new Set(fields)].join(', ')}` : result.error;
};

/**
 * One disclosure the import couldn't create, editable in place. Saving sends
 * the corrected values WITH the original file back through the import
 * endpoint, so it gets the same validation, duplicate check and upload as a
 * clean import, and works even while the reader is down (ADR-0013).
 */
const EscrowFixCard = ({ item, mortgageId, onSaved, onSkip }) => {
  const fields = item.fields || {};
  const [values, setValues] = useState(() => ({
    cycle_start: toCycleInputValue(fields.cycle_start),
    statement_date: toDateInputValue(fields.statement_date),
    // Formatted like MoneyInput displays it, so what is saved is what is shown
    // (a raw 9213.456 would show as 9213.46 but be rejected as sent).
    ...Object.fromEntries(MONEY_FIELDS.map(([key]) => [key, formatMoney(fields[key])])),
  }));
  const [saving, setSaving] = useState(false);
  const [inlineError, setInlineError] = useState(null);

  const fieldId = (key) => `escrow-fix-${item.clientId}-${key}`;
  const setField = (key) => (e) => setValues((prev) => ({ ...prev, [key]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (!values.cycle_start || !values.new_escrow_payment) {
      setInlineError('New payment start and new escrow payment are required');
      return;
    }
    const badMoney = MONEY_FIELDS.find(([key]) => values[key] !== '' && !isNumeric(values[key]));
    if (badMoney) {
      setInlineError(`${badMoney[1]} must be a number`);
      return;
    }

    const carried = Object.fromEntries(
      CARRIED_MONEY_FIELDS.map((key) => [key, toSubmittableMoney(fields[key])]).filter(([, value]) => value != null),
    );
    const payload = {
      ...carried,
      disbursements: Array.isArray(fields.disbursements) ? fields.disbursements : null,
      cycle_start: values.cycle_start,
      // Blank optional values go as null: the server stores NULL, never ''.
      statement_date: values.statement_date || null,
      ...Object.fromEntries(MONEY_FIELDS.map(([key]) => [key, values[key] === '' ? null : Number(values[key])])),
    };

    try {
      setSaving(true);
      setInlineError(null);
      const result = await MortgageService.importEscrowDisclosure(mortgageId, item.file, {
        clientId: item.clientId,
        fields: payload,
      });
      if (result.outcome === 'created') {
        onSaved(item.clientId, result.disclosure);
        return;
      }
      if (result.outcome === 'skipped_duplicate') {
        setInlineError(
          `You already have the disclosure for ${formatCycle(values.cycle_start)}. Skip this file, or change the date.`,
        );
        return;
      }
      setInlineError(problemText(result) || 'Could not save this disclosure');
    } catch (err) {
      setInlineError(err.message || 'Could not save this disclosure');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      aria-label={`Fix ${item.filename}`}
      onSubmit={handleSave}
      className="p-4 rounded-xl border border-[var(--border)] bg-[var(--bg-hover)] mb-3"
    >
      <div className="flex items-start gap-2 mb-3 min-w-0">
        <AlertCircle size={16} className="text-[var(--error)] shrink-0 mt-0.5" />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-main)] truncate">{item.filename}</p>
          <p className="text-xs text-[var(--text-secondary)]">{item.error}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor={fieldId('cycle_start')} className={LABEL_CLASS}>
            New Payment Starts *
          </label>
          <Input
            id={fieldId('cycle_start')}
            type="date"
            value={values.cycle_start}
            onChange={setField('cycle_start')}
          />
        </div>
        <div>
          <label htmlFor={fieldId('statement_date')} className={LABEL_CLASS}>
            Analysis Date
          </label>
          <Input
            id={fieldId('statement_date')}
            type="date"
            value={values.statement_date}
            onChange={setField('statement_date')}
          />
        </div>
        {MONEY_FIELDS.map(([key, label, opts = {}]) => (
          <div key={key}>
            <label htmlFor={fieldId(key)} className={LABEL_CLASS}>
              {opts.required ? `${label} *` : label}
            </label>
            <MoneyInput id={fieldId(key)} name={key} value={values[key]} onChange={setField(key)} />
          </div>
        ))}
      </div>

      {inlineError && (
        <p className="text-xs text-[var(--error)] mt-3" role="alert">
          {inlineError}
        </p>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-end gap-2 mt-4">
        <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={() => onSkip(item.clientId)}>
          Skip
        </Button>
        <Button type="submit" loading={saving} className="w-full sm:w-auto">
          Save disclosure
        </Button>
      </div>
    </form>
  );
};

/**
 * Bulk escrow disclosure import (ADR-0013): drop the servicer's annual escrow
 * statements, come back to one row per escrow cycle. Shares the runner, queue
 * and summary with the bill and statement imports (BulkImportPanel).
 */
const BulkEscrowImport = ({ mortgageId, onDisclosuresCreated, onClose }) => (
  <BulkImportPanel
    title="Escrow Disclosures"
    noun={{ one: 'disclosure', many: 'disclosures' }}
    dropTitle="Drop your escrow disclosure PDFs here"
    dropHint="One file per annual escrow statement. We read the coming year's projection from each — you only get asked about files we can't read."
    inputTestId="bulk-escrow-file-picker-input"
    unavailableMessage="Escrow disclosure reading is unavailable right now — try again in a few minutes"
    importFile={(file, opts) => MortgageService.importEscrowDisclosure(mortgageId, file, opts)}
    recordOf={(result) => result.disclosure}
    onRecordsCreated={onDisclosuresCreated}
    renderFixCard={({ item, onSaved, onSkip }) => (
      <EscrowFixCard item={item} mortgageId={mortgageId} onSaved={onSaved} onSkip={onSkip} />
    )}
    onClose={onClose}
  />
);

export default BulkEscrowImport;
